// Clave de la obra activa recordada (#252). Mismo patrón que el plegado de la barra
// (useSidebar.ts): una preferencia de navegación del dispositivo, no un dato de dominio.
// Se borra en login, en logout y en la purga por 401 para que en una tablet compartida un
// usuario no herede la obra del anterior.
//
// D11: la PERSISTENCIA vive en `lib/` y la RESOLUCIÓN (validar contra `/me`, traducir rutas,
// el hook) en `app/obra-activa.ts`. Así `lib/auth.ts` y `main.tsx` pueden olvidar la obra sin
// que `lib/` importe de `app/`, que invertiría la estratificación del frontend.
export const OBRA_ACTIVA_KEY = "loto.obra.activa";

// localStorage no es reactivo: los suscriptores permiten que la barra y el conmutador se
// repinten al recordar u olvidar la obra sin cambiar de ruta (cambiar de obra desde /usuarios,
// por ejemplo). Desde #265 cubren también las OTRAS pestañas, por el evento `storage`.
const suscriptores = new Set<() => void>();

/**
 * alCambiarOtraPestana propaga a esta pestaña lo que otra recordó u olvidó (#265 D3). El
 * navegador emite `storage` solo en las pestañas que NO escribieron, y para entonces el valor
 * ya está en el localStorage compartido, así que basta con avisar: los suscriptores releen con
 * `leerObraRecordada` (un `newValue` nulo —el logout de la otra pestaña— se lee como olvido).
 * Se filtra por la clave, mismo patrón que el plegado de la barra (useSidebar.ts).
 *
 * La obra que llega así NO es más fiable por venir de otra pestaña: quien la resuelve
 * (`app/obra-activa.ts`) la sigue revalidando contra `/me` (D4).
 */
function alCambiarOtraPestana(evento: StorageEvent): void {
  if (evento.key !== OBRA_ACTIVA_KEY) {
    return;
  }
  notificar();
}

/**
 * suscribirObraActiva avisa cuando la obra recordada cambia, aquí o en otra pestaña. Devuelve
 * la baja. El listener de `storage` se engancha con el primer suscriptor y se retira con el
 * último, de modo que no queda colgado al desmontar.
 */
export function suscribirObraActiva(alCambiar: () => void): () => void {
  if (suscriptores.size === 0) {
    window.addEventListener("storage", alCambiarOtraPestana);
  }
  suscriptores.add(alCambiar);
  return () => {
    suscriptores.delete(alCambiar);
    if (suscriptores.size === 0) {
      window.removeEventListener("storage", alCambiarOtraPestana);
    }
  };
}

function notificar(): void {
  for (const alCambiar of suscriptores) {
    alCambiar();
  }
}

/**
 * leerObraRecordada devuelve la obra recordada en este navegador. Un valor ausente,
 * corrupto o no positivo se trata como «ninguna»: la obra siempre se valida después
 * contra el `/me` (`resolverObraRecordada`), así que aquí basta con sanear el formato.
 */
export function leerObraRecordada(): number | undefined {
  let bruto: string | null = null;
  try {
    bruto = localStorage.getItem(OBRA_ACTIVA_KEY);
  } catch {
    // localStorage falla en modo privado de algunos navegadores: sin obra recordada
    // la navegación sigue funcionando (la ruta manda).
    return undefined;
  }
  if (bruto === null) {
    return undefined;
  }
  const id = Number(bruto);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

/** recordarObraActiva persiste la obra activa en este navegador. */
export function recordarObraActiva(id: number): void {
  try {
    localStorage.setItem(OBRA_ACTIVA_KEY, String(id));
  } catch {
    // Preferencia no persistida: no es motivo para romper la navegación.
  }
  notificar();
}

/** olvidarObraActiva borra la obra recordada (login, logout y purga por 401). */
export function olvidarObraActiva(): void {
  try {
    localStorage.removeItem(OBRA_ACTIVA_KEY);
  } catch {
    // Best-effort, igual que la purga de la caché offline que la acompaña.
  }
  notificar();
}
